<template>
  <div class="zw introduction">
    <div class="setup">
      <div class="content">
        <transition name="swipe">
          <div class="slider-content" v-if="currentTick('profile-setup')" :key="ticks[activeTick].name"
               :class="clickedClass">
            <h1 class="ui header">
              {{ $t('pages.setup.content.profilesetup.header') }}
              <div class="sub header">{{ $t('pages.setup.content.profilesetup.subtext') }}</div>
            </h1>
            <profile-setup ref="ProfileSetup"></profile-setup>
          </div>

          <div class="slider-content" v-if="currentTick('social-number-setup')" :key="ticks[activeTick].name"
               :class="clickedClass">
            <h1 class="ui header">
              {{ $t('pages.setup.content.socialNumberSetup.header') }}
              <div class="sub header">{{ $t('pages.setup.content.socialNumberSetup.subtext') }}</div>
            </h1>
            <social-number-setup ref="SocialNumberSetup"></social-number-setup>
          </div>

          <div class="slider-content" v-if="currentTick('company-setup')" :key="ticks[activeTick].name"
               :class="clickedClass">
            <h1 class="ui header">
              {{ $t('pages.setup.content.companysetup.header') }}
              <div class="sub header">{{ $t('pages.setup.content.companysetup.subtext') }}</div>
            </h1>
            <company-setup ref="CompanySetup"></company-setup>
          </div>

          <div class="slider-content" v-else-if="currentTick('first-service')" :key="ticks[activeTick].name"
               :class="clickedClass">
            <h1 class="ui header">
              {{ $t('pages.setup.content.firstservice.header') }}
              <div class="sub header">{{ $t('pages.setup.content.firstservice.subtext') }}</div>
            </h1>

            <h4 class="header" v-html="$t('pages.setup.content.firstservice.text')"></h4>

            <template v-if="isExternalUser">
              <div class="ui basic center aligned segment">
                <div class="ui blue rounded large button" @click="showAvailableProductsModal = !showAvailableProductsModal">
                  {{ $t('pages.setup.content.firstservice.button') }}
                </div>
              </div>
              <available-products-modal :active="showAvailableProductsModal" :intro="true"></available-products-modal>
            </template>
            <template v-else>
              <div class="ui basic center aligned segment">
                <div class="ui blue rounded large button" @click="toggleSubscriptionModal">
                  {{ $t('pages.setup.content.firstservice.button') }}
                </div>
              </div>
              <new-subscription-modal :active="showFirstSubscriptionModal" :subscription="firstSubscription"
                                      :intro="true"></new-subscription-modal>
            </template>
          </div>
        </transition>
      </div>
      <div class="actions">
        <div class="ui large rounded icon labeled button" @click="prevTick()" v-if="showPrev">
          <i class="angle left icon"/> {{ $t('globals.buttons.previous') }}
        </div>
        <div class="ui blue rounded right labeled large icon button" @click="nextTick()" v-if="showNext">
          <i class="angle right icon"/> {{ $t('globals.buttons.next_step') }}
        </div>
        <div class="ui blue rounded large icon button" @click="finishSetup" :class="{ 'disabled' : !finalize }"
             v-else-if="!showNext">
          {{ $t('globals.buttons.finalize') }}
        </div>
      </div>
    </div>

    <div class="menu" :class="{ 'fullscreen' : ticks[activeTick].fullscreen }">
      <div class="content">
        <div class="process">
          <div class="ticks">
            <div class="tick" :class="{ 'active' : tickdex === activeTick }" v-for="(tick, tickdex) in ticks"
                 :key="tickdex" @click="switchTick(tickdex)">
              {{ tickdex + 1 }}
            </div>
          </div>
        </div>
        <div class="middle">
          <transition name="swipe">
            <div class="slider-content" :class="clickedClass" v-for="(tick, index) in ticks" :key="index"
                 v-if="index === activeTick">
              <div class="ui header" :class="{ 'icon' : !!tick.icon }">
                <i class="icon" :class="tick.icon" v-if="tick.icon"/> {{ tick.header }}
              </div>

              <div class="description" v-html="tick.description"></div>

              <div class="ui blue rounded right labeled large icon button" @click="nextTick()" v-if="tick.button">
                {{ tick.button }}
                <i class="angle right icon"/>
              </div>
            </div>
          </transition>
        </div>
        <div class="information">
          <h4 v-if="activeMerchant">{{ this.merchantPhone }}</h4>
          <h4 v-if="activeMerchant" style="word-break: break-all;">{{ this.merchantEmail }}</h4>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import size from 'lodash/size'
import findIndex from 'lodash/findIndex'

/*
 * Pages available:
 *
 * get-started - A welcome page, with a get started button
 * profile-setup - Setting up the user profile
 * company-setup - Setting up the company profile /optional
 * first-service - Setting up the first subscription
 *
 */

export default {
  name: 'UserSetup',

  layout: 'intro',

  data() {
    return {
      fullscreen: true,

      showPrev: false,
      showNext: true,
      activeTick: 0,
      currentPage: this.$route.params.process,
      finalize: true,

      ticks: [
        {
          name: null,
          header: null,
          description: null,
          button: null,
          icon: null,
          fullscreen: true
        }
      ],

      clickedClass: false,

      firstSubscription: null,
      showFirstSubscriptionModal: false,
      showAvailableProductsModal: false
    }
  },

  mounted() {
    this.initTicks().then((data) => {
      this.ticks = data

      this.$nextTick(() => {
        this.switchTick(_.findIndex(this.ticks, {name: this.currentPage}))
      })
    })

    this.$events.$on('updatesubscriptions', (data) => {
      this.finalize = true
      this.finishSetup()
    })
  },

  watch: {
    activeTick () {
      this.checkButtons()
    }
  },

  methods: {
    checkButtons() {
      this.showNext = this.activeTick !== this.ticks.length - 1
      this.showPrev = this.activeTick !== 0
    },

    initTicks () {
      return new Promise((resolve, reject) => {
        this.ticks = []

        this.ticks.push(
          {
            name: 'get-started',
            header: this.$t('pages.setup.menu.getstarted.header'),
            description: this.$t('pages.setup.menu.getstarted.description', {0: this.$auth.user().firstname}),
            button: this.$t('pages.setup.menu.getstarted.button'),
            icon: 'blue handshake',
            fullscreen: true
          },
          {
            name: 'profile-setup',
            header: this.$t('pages.setup.menu.profilesetup.header'),
            description: this.$t('pages.setup.menu.profilesetup.description'),
            icon: 'id card outline blue',
            fullscreen: false
          },
          {
            name: 'social-number-setup',
            header: this.$t('pages.setup.menu.socialNumberSetup.header'),
            description: this.$t('pages.setup.menu.socialNumberSetup.description'),
            icon: 'id card outline blue',
            fullscreen: false
          },
          {
            name: 'company-setup',
            header: this.$t('pages.setup.menu.companysetup.header'),
            description: this.$t('pages.setup.menu.companysetup.description'),
            icon: 'travel blue',
            fullscreen: false
          },
          {
            name: 'first-service',
            header: this.$t('pages.setup.menu.firstservice.header'),
            description: this.$t('pages.setup.menu.firstservice.description'),
            icon: 'browser outline blue',
            fullscreen: false
          }
        )

        const _this = this

        let filteredTicks = this.ticks.filter((value, index) => {
          if (value.name === 'get-started') {
            // Always return the first get-started screen
            return value
          } else if (value.name === 'profile-setup') {
            return value
          } else if (value.name === 'social-number-setup') {
            // Show social number setup screen only if merchant has opus accounting system enabled
            if (_this.$store.getters.activeMerchant && _this.$store.getters.activeMerchant.opus) {
              return value
            }
          } else if (value.name === 'company-setup') {
            return value
          } else if (value.name === 'first-service') {
            return value
          }
        })

        resolve(filteredTicks)
      });
    },

    switchTick(index) {
      this.clickedClass = false

      if (this.activeTick > index) {
        this.clickedClass = 'prev'
      } else if (this.activeTick < index) {
        this.clickedClass = 'next'
      }

      if (this.ticks[this.activeTick].name === 'profile-setup') {
        this.$refs.ProfileSetup.validateFields('profile-setup')
          .then(validation => {
            if (validation) {
              this.$nextTick(() => {
                this.activeTick = index
                this.$router.replace({params: {process: this.ticks[this.activeTick].name}})
              })
            }
          })
      } else if (this.ticks[this.activeTick].name === 'social-number-setup') {
        this.$refs.SocialNumberSetup.saveSocialNumber('social-number-setup')
          .then(validation => {
            if (validation) {
              this.$nextTick(() => {
                this.activeTick = index
                this.$router.replace({params: {process: this.ticks[this.activeTick].name}})
              })
            }
          })
      } else if (this.ticks[this.activeTick].name === 'company-setup') {
        this.$refs.CompanySetup.validateFields('company-setup')
          .then(validation => {
            if (validation) {
              this.$nextTick(() => {
                this.activeTick = index
                this.$router.replace({params: {process: this.ticks[this.activeTick].name}})
              })
            }
          })
      } else {
        this.$nextTick(() => {
          this.activeTick = index
          this.$router.replace({params: {process: this.ticks[this.activeTick].name}})
        })
      }
    },

    nextTick() {
      const nextTick = this.activeTick + 1
      if (size(this.ticks[nextTick]) > 0) {
        this.switchTick(nextTick)
      }
    },

    prevTick() {
      const nextTick = this.activeTick - 1
      if (size(this.ticks[nextTick]) > 0) {
        this.switchTick(nextTick)
      }
    },

    currentTick(string) {
      return this.activeTick === findIndex(this.ticks, {name: string});
    },

    fetchUnsetSubscription() {
      return new Promise((resolve, reject) => {
        this.axios.get(`/merchants/${this.$store.getters.activeMerchant.id}/subscriptions`, {
          params: {
            page: 1,
            perPage: 1,
            filter: 'unset|activating|deletable'
          }
        })
          .then(({data}) => {
            if (data.total >= 1) {
              this.unsetSubscription = null
              const subscriptionId = data.data[0].id
              this.axios.get(`subscriptions/${subscriptionId}`)
                .then(({data}) => {
                  resolve(data)
                }).catch(error => {
                reject(error)
              })
            } else {
              reject(false)
            }
          })
      })
    },

    toggleSubscriptionModal() {
      this.fetchUnsetSubscription()
        .then(Subscription => {
          this.firstSubscription = Subscription
          this.$nextTick(() => {
            this.showFirstSubscriptionModal = !this.showFirstSubscriptionModal
          })
        }).catch(() => {
        this.finalize = true
      })
    },

    finishSetup() {
      if (this.$route.name === 'usersetup') {
        this.$router.push({name: 'setupsuccess'})
      }
    }
  },

  computed: {
    process() {
      return this.$route.params.process
    },

    isExternalUser() {
      return this.$auth.user().external_user;
    },

    activeMerchant() {
      return this.$store.getters.activeMerchant
    },

    merchantPhone() {
      return this.$store.getters.activeMerchant.phone
    },

    merchantEmail() {
      return this.$store.getters.activeMerchant.email
    },

    lastTick() {
      const nextTick = this.activeTick + 1

      return size(this.ticks[nextTick]) > 0;
    }
  },

  beforeDestroy() {
    this.$events.$off('updatesubscriptions')
  }
}
</script>

<style lang="scss">
.zw {
  &.introduction {
    display: flex;
    position: relative;
    width: 85%;
    height: 90%;
    box-shadow: -5px -2px 35px 0px rgba(0, 0, 0, 0.14), 4px 20px 35px rgba(0, 0, 0, 0.02);
    color: rgb(145, 163, 174);
    justify-content: flex-end;
    overflow: hidden;

    @media only screen and (max-width: 768px) {
      width: 95%;
      height: 95%;
    }

    @media only screen and (max-width: 425px) {
      width: 100%;
      height: 100%;
    }

    .menu {
      width: 100%;
      display: flex;
      position: absolute;
      background: #fff;
      align-items: center;
      height: 100%;
      width: 320px;
      right: 0;
      justify-content: center;
      transition: width 1s ease;
      box-shadow: -5px -2px 35px 0px rgba(0, 0, 0, 0.14), 4px 20px 35px rgba(0, 0, 0, 0.02);

      @media only screen and (max-width: 768px) {
        width: 280px;
      }

      @media only screen and (max-width: 425px) {
        width: 0;
        overflow: hidden;
      }

      &.fullscreen {
        width: 100%;

        .content {
          @media only screen and (max-width: 425px) {
            opacity: 1;
            overflow: hidden;
            width: 100%;
          }
        }
      }

      .content {
        padding: 40px;
        height: 100%;
        display: flex;
        justify-content: space-between;
        flex-flow: column;
        width: 320px;
        overflow: hidden;
        opacity: 1;
        transition: opacity 0.2s ease;

        @media only screen and (max-width: 768px) {
          width: 280px;
        }

        @media only screen and (max-width: 425px) {
          opacity: 0;
          overflow: hidden;
        }

        .process {
          position: relative;
          width: 100%;
          padding-bottom: 40px;

          .ticks {
            display: flex;
            justify-content: center;
            align-items: center;

            .tick {
              position: relative;
              display: flex;
              align-items: center;
              justify-content: center;
              height: 15px;
              width: 15px;
              padding: 20px;
              font-weight: 700;
              border: 2px solid transparent;
              border-radius: 50%;

              transition: all 0.5s ease;

              &:hover {
                cursor: pointer;
              }

              &.active {
                border: 2px solid #0ba8f5;
                color: #0ba8f5;
                border-radius: 50%;
              }

              &.completed {
                color: #D7E4ED;
              }
            }
          }

          &:after {
            content: "";
            position: absolute;
            display: block;
            bottom: 0;
            width: 100%;
            transform: scaleX(.08);
            border-bottom: 5px solid #D7E4ED;
          }
        }

        .middle {
          position: relative;
          width: 100%;
          display: flex;
          height: 100%;
          align-items: center;
          justify-content: center;

          .slider-content {
            height: 100%;
            width: 100%;
            text-align: center;
            display: flex;
            flex-flow: column;
            position: absolute;
            justify-content: center;
            top: 0;
            left: 0;

            .description {
              font-size: 18px;
              font-weight: 400;
              line-height: 30px;
            }

            .button {
              margin-top: 40px;
            }
          }
        }

        .information {
          width: 100%;
          text-align: center;
          padding-top: 40px;
          position: relative;

          h4 {
            margin: 0;
            font-weight: 600;
          }

          &:before {
            content: "";
            position: absolute;
            display: block;
            top: 0;
            width: 100%;
            transform: scaleX(.08);
            border-top: 5px solid #D7E4ED;
          }
        }
      }
    }

    .setup {
      padding: 20px;
      position: relative;
      width: 100%;
      margin-right: 320px;
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      flex-flow: column;
      overflow-y: auto;

      @media only screen and (max-width: 768px) {
        margin-right: 280px;
        padding: 10px 0;
      }

      @media only screen and (max-width: 425px) {
        margin-right: 0;
        padding: 10px 0;
      }

      .content {
        display: inline-flex;
        width: 100%;
        flex-flow: column;
        position: relative;
        justify-content: flex-start;
        align-items: center;
        min-height: 85%;
        overflow-y: auto;
        overflow-x: hidden;

        iframe {
          height: 350px;

          @media only screen and (max-width: 768px) {
            height: 400px;
          }

          @media only screen and (max-width: 425px) {
            height: 400px;
          }
        }

        .slider-content {
          padding: 30px;
          position: absolute;
          width: 100%;
          display: flex;
          flex-flow: column;
          left: 0;

          @media only screen and (max-width: 768px) {
            padding: 10px;
          }

          @media only screen and (max-width: 425px) {
            padding: 10px;
          }

          .top-bordered.action {
            margin-top: auto;
            display: flex;
            align-items: center;
            flex-flow: column;
            justify-content: center;

            a {
              color: #5CABED;
              text-decoration: underline;

              &:hover {
                color: rgb(118, 193, 255);
                cursor: pointer;
              }
            }
          }
        }
      }

      .actions {
        padding: 30px;
        width: 100%;
        margin-top: auto;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        min-height: 15%;

        @media only screen and (max-width: 768px) {
          padding: 5px;
          .button {
            font-size: 1rem;
          }
        }

        @media only screen and (max-width: 425px) {
          padding: 5px;

          .button {
            font-size: 1rem;
          }
        }
      }
    }
  }
}

.swipe-leave-active,
.swipe-enter-active {
  transition: 1s ease;
}

.swipe-enter.prev {
  transform: translate(-120%, 0);
}

.swipe-leave-to.prev {
  transform: translate(120%, 0);
}

.swipe-enter.next {
  transform: translate(120%, 0);
}

.swipe-leave-to.next {
  transform: translate(-120%, 0);
}
</style>



// WEBPACK FOOTER //
// src/pages/setup/UserSetup.vue